import type { FC } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';
import { SortableStopCard } from "./SortableStopCard";
import { useTranslation } from "react-i18next";
import { EnhancedRouteItem } from "./types";
import { HiChevronDown, HiChevronRight, HiOfficeBuilding } from "react-icons/hi";
import { useState } from "react";
import { cn, btnSecondary, Stack } from "@design-system";

interface SortableOrderGroupProps {
  orderId: string;
  groupStops: EnhancedRouteItem[];
  orderDisplayName: string;
  onMoveToRoute?: (stopId: string) => void;
  onMoveToAvailable?: (stopId: string) => void;
  onMoveOrderToRoute?: (orderId: string) => void;
  invalidStopIds?: string[];
}

export const SortableOrderGroup: FC<SortableOrderGroupProps> = ({ 
  orderId, 
  groupStops, 
  orderDisplayName, 
  onMoveToRoute, 
  onMoveToAvailable, 
  onMoveOrderToRoute,
  invalidStopIds = [] 
}) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `group-${orderId}`,
    data: {
      id: `group-${orderId}`,
      isAvailable: true, // Order groups are always in the available panel
      orderId: orderId
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const hasInvalidStops = groupStops.some(stop => invalidStopIds.includes(stop.location.id));

  const handleAddEntireOrder = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle
    if (onMoveOrderToRoute) {
      onMoveOrderToRoute(orderId);
    }
  };

  const handleToggleCollapse = (e: React.MouseEvent) => {
    // Only toggle if we didn't click on the plus button
    if ((e.target as HTMLElement).closest('.add-order-button')) {
      return;
    }
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex flex-col rounded-lg border border-border overflow-hidden",
        "transition-all duration-150 ease-in-out hover:border-border-strong bg-surface/50",
        isDragging && "opacity-70 scale-[1.02] shadow-lg z-10",
        hasInvalidStops && "border-red-200 dark:border-red-700",
      )}
    >
      <div 
        {...attributes} 
        {...listeners}
        onClick={handleToggleCollapse}
        className="text-sm font-medium text-secondary p-2 cursor-move hover:text-text select-none flex items-center gap-2 bg-surface-muted border-b border-border group transition-colors"
      >
        {isCollapsed ? (
          <HiChevronRight className="h-4 w-4 text-muted group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
        ) : (
          <HiChevronDown className="h-4 w-4 text-muted group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
        )}
        <HiOfficeBuilding className="h-4 w-4 text-muted" />
        <div className="flex-1 truncate">{orderDisplayName}</div>
        <div className="text-xs text-gray-500 bg-surface-muted px-1.5 py-0.5 rounded-full">
          {groupStops.length} {groupStops.length === 1 ? t('planner:route_planner.stop') : t('planner:route_planner.stops')}
        </div>
        
        {/* Add entire order button */}
        {onMoveOrderToRoute && (
          <button
            type="button"
            onClick={handleAddEntireOrder}
            className={cn(btnSecondary, "add-order-button px-2 py-1 text-xs rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500")}
            aria-label={t('planner:route_planner.add_entire_order', 'Add entire order to route')}
            title={t('planner:route_planner.add_entire_order', 'Add entire order to route')}
          >
            {t('common:add', 'Legg til')}
          </button>
        )}
      </div>
      
      <Stack gap={2} className={cn("p-2", isCollapsed ? "hidden" : "block")}>
        {groupStops.map((stop) => (
          <SortableStopCard 
            key={stop.location.id} 
            stop={stop} 
            onMoveToRoute={onMoveToRoute ? () => onMoveToRoute(stop.location.id) : undefined}
            onMoveToAvailable={onMoveToAvailable ? () => onMoveToAvailable(stop.location.id) : undefined}
            isInvalid={invalidStopIds?.includes(stop.location.id)}
            routeStops={groupStops}
          />
        ))}
      </Stack>
    </div>
  );
}; 